/**
 * 活小镇首页（01 开发文档 + 定稿原型 town.js 逐行移植，工程结构 §2.8）。
 *
 * 移植说明（三类允许改动，均记录于 M3 实现说明）：
 * - 接线替换：ENTRANCES/BUILDING_DEPTH 硬编码 → GET /public/town（hotspots 投影）+
 *   GET /public/builders（npcPos）；builders DTO → PublicBuilder[]；?lang= → useLocale()；
 *   location.assign → React Router navigate；link() → /{locale} 路径。
 * - 框架适配：DOM 查询/手动初始化 → useRef + useEffect 初始化（内部逻辑/时序参数原样）；
 *   对话框用原生 <dialog>（原型同款 showModal）。
 * - 数据来源：入口 copy/label 来自 API（fixture description）；夜里图片 URL 来自
 *   API dayAsset/nightAsset（前端自托管 public/town/）。
 *
 * 原样保留：SVG clipPath 本体裁切、occluderSpans 底线预计算、walker DOM 结构与
 * 内联 SVG、--stride/--bob 变量、180ms 点击反馈、showModal 焦点圈定、夜灯
 * lightLevel 波动、occluder 深度排序、resize 等比缩放（cover 逻辑）、localStorage key。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { BuildingEntrance, PublicBuilder } from '@chronospeak/contracts';
import type { Envelope } from '../lib/api-types';
import { http } from '../lib/http';
import { useLocale, track } from '../lib/locale';
import { DraftBadge, LocaleFallbackNotice } from '../components/bilingual';
import type { LocaleMeta } from '@chronospeak/contracts';
import '../features/town/town.css';

import menuIcon from 'lucide-static/icons/menu.svg';
import guideIcon from 'lucide-static/icons/compass.svg';
import moonIcon from 'lucide-static/icons/moon.svg';
import sunIcon from 'lucide-static/icons/sun.svg';
import pauseIcon from 'lucide-static/icons/pause.svg';
import playIcon from 'lucide-static/icons/play.svg';
import closeIcon from 'lucide-static/icons/x.svg';
import leftIcon from 'lucide-static/icons/arrow-left.svg';
import rightIcon from 'lucide-static/icons/arrow-right.svg';

import { createCrowd } from '../features/town/town-navigation.js';
import { ROAD_OCCLUDERS } from '../features/town/town-occlusion.js';
import { SIZE, lightLevel } from '../features/town/town-model.js';
import { WelcomeLetter } from '../features/letter/WelcomeLetter';

/* ---------------- 场景 DTO 类型（GET /public/town） ---------------- */
interface TownDto {
  sceneVersion: string;
  dayAsset: string;
  nightAsset: string;
  hotspots: BuildingEntrance[];
  npcs: { slug: string; name: string; role: string; npcPos: { x: number; y: number }; sortOrder: number }[];
}

/* ---------------- 原型工具（town.js 1:1） ---------------- */
const storage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* 隐私模式静默 */
    }
  },
};

const reduced = matchMedia('(prefers-reduced-motion: reduce)');

/** 原型 parsePathPoints（occluder 底线预计算用） */
function parsePathPoints(d: string): [number, number][] {
  return (d.match(/[-\d.]+[, ]+[-\d.]+/g) ?? []).map((pair) => pair.split(/[, ]+/).map(Number) as [number, number]);
}

type OccluderSpan = {
  key: string;
  depth: number;
  x0: number;
  x1: number;
  edges: Map<number, number>;
};

/** 原型 occluderSpans 预计算（每整数 x 的最大边缘 y = 接地线） */
function buildOccluderSpans(): OccluderSpan[] {
  return ROAD_OCCLUDERS.map((e) => {
    const pts = parsePathPoints(e.path);
    const edges = new Map<number, number>();
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = pts[i]!;
      const [bx, by] = pts[(i + 1) % pts.length]!;
      const [x0, x1] = [Math.min(ax, bx), Math.max(ax, bx)];
      for (let x = Math.ceil(x0); x <= x1; x++) {
        const y = Math.abs(bx - ax) < 1e-6 ? Math.max(ay, by) : ay + ((by - ay) * (x - ax)) / (bx - ax);
        const prev = edges.get(x);
        // Only lower (southern) edges are ground-contact lines; edges seen from
        // above (roofs) have smaller y and must not win.
        if (prev === undefined || y > prev) edges.set(x, y);
      }
    }
    let x0 = 1e9;
    let x1 = -1e9;
    for (const x of edges.keys()) {
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
    }
    return { key: e.key, depth: e.depth, x0, x1, edges };
  });
}

/* ---------------- walker 内联 SVG（原型 personHTML 1:1） ---------------- */
const COATS = ['#3b4844', '#756055', '#a08c70', '#333c37', '#67796c'];
function personSVG(i: number): string {
  const coat = COATS[i % 5]!;
  return `<svg viewBox="0 0 24 40" aria-hidden="true"><ellipse cx="17" cy="38" rx="11" ry="2.2" fill="#252c2940"/><g class="figure"><path class="leg leg-a" d="M10 24 L9 36" stroke="#353b38" stroke-width="3" stroke-linecap="round"/><path class="leg leg-b" d="M14 24 L15 36" stroke="#454940" stroke-width="3" stroke-linecap="round"/><path d="M9 12 Q12 10 15 12 L17 25 Q12 28 7 25 Z" fill="${coat}"/><path class="arm" d="M8 14 L6 24 M16 14 L18 24" fill="none" stroke="${coat}" stroke-width="2.4" stroke-linecap="round"/><circle cx="12" cy="8" r="3.4" fill="#b39c83"/><path d="M8.5 8 Q8 2 12 3 Q16 3 15.5 8" fill="#393c33"/>${i === 0 ? '<path d="M8 6h10" stroke="#6d7668" stroke-width="2"/>' : i === 1 ? '<circle cx="14" cy="3" r="2.3" fill="#373a31"/>' : i === 2 ? '<path d="M9 12l5 8" stroke="#c2b29b" stroke-width="2"/>' : ''}</g></svg>`;
}

/* ---------------- 图标按钮工具（原型 icon() 1:1） ---------------- */
function setIcon(button: HTMLElement, url: string, label?: string): void {
  button.innerHTML = `<img src="${url}" alt="" />`;
  if (label) {
    button.title = label;
    button.setAttribute('aria-label', label);
  }
}

type PersonState = 'pause' | 'walk' | 'entering' | 'exiting' | 'read-notice' | 'indoors';

interface PersonMotion {
  x: number;
  y: number;
  dx: number;
  dy: number;
  gait: number;
  state: PersonState;
  alpha: number;
  held: boolean;
  wait: number;
  path: number[][];
  index: number;
  goal: string;
  speed: number;
  age: number;
  stuck: number;
  lastX: number;
  lastY: number;
  id: number;
}

interface PersonEntry {
  button: HTMLButtonElement | HTMLSpanElement;
  record: PublicBuilder | null;
  motion: PersonMotion;
  hover: boolean;
  focused: boolean;
}

const TOWN = {
  /** 原型 44/48px 命区（walker 按钮） */
  WALKER_W: 44,
  WALKER_H: 48,
};

export function TownPage() {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const en = locale === 'en';
  const text = useCallback((zh: string, english: string) => (en ? english : zh), [en]);

  const rootRef = useRef<HTMLElement | null>(null);
  const worldRef = useRef<HTMLDivElement | null>(null);
  const architectureRef = useRef<SVGSVGElement | null>(null);
  const peopleRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLImageElement | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef<HTMLParagraphElement | null>(null);
  const menuDialogRef = useRef<HTMLDialogElement | null>(null);
  const guideDialogRef = useRef<HTMLDialogElement | null>(null);
  const profileDialogRef = useRef<HTMLDialogElement | null>(null);
  const menuLinksRef = useRef<HTMLElement | null>(null);
  const guideCountRef = useRef<HTMLSpanElement | null>(null);
  const guideTitleRef = useRef<HTMLHeadingElement | null>(null);
  const guideCopyRef = useRef<HTMLParagraphElement | null>(null);
  const guideEnterRef = useRef<HTMLAnchorElement | null>(null);
  const profileNameRef = useRef<HTMLHeadingElement | null>(null);
  const profileRoleRef = useRef<HTMLParagraphElement | null>(null);
  const profileStatementRef = useRef<HTMLParagraphElement | null>(null);
  const profileLinkRef = useRef<HTMLAnchorElement | null>(null);
  const profileCanvasRef = useRef<HTMLDivElement | null>(null);
  const profileStaticRef = useRef<HTMLDivElement | null>(null);
  const profileStaticNameRef = useRef<HTMLElement | null>(null);

  // 控件按钮 refs
  const langBtnRef = useRef<HTMLButtonElement | null>(null);
  const themeBtnRef = useRef<HTMLButtonElement | null>(null);
  const motionBtnRef = useRef<HTMLButtonElement | null>(null);
  const openMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const openGuideBtnRef = useRef<HTMLButtonElement | null>(null);
  const prevBtnRef = useRef<HTMLButtonElement | null>(null);
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);
  const closeBtnsRef = useRef<HTMLButtonElement[]>([]);

  const [town, setTown] = useState<Envelope<TownDto> | null>(null);
  const [builders, setBuilders] = useState<PublicBuilder[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [night, setNight] = useState(() => storage.get('chronospeak-night') === '1');

  /* ---------------- 数据装载（接线替换：原型 builders DTO → API） ---------------- */
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    Promise.all([
      http.get<Envelope<TownDto>>(`/public/town?locale=${locale}`),
      http.get<Envelope<{ items: PublicBuilder[] }>>(`/public/builders?locale=${locale}`),
    ])
      .then(([t, b]) => {
        if (cancelled) return;
        setTown(t);
        setBuilders(b.data.items);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // 埋点 town_viewed（09 白名单：locale/theme/device/degraded）
  useEffect(() => {
    if (!town) return;
    track('town_viewed', {
      locale,
      theme: night ? 'night' : 'day',
      device: window.innerWidth < 768 ? 'mobile' : 'desktop',
      degraded: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [town]);

  const hotspots = useMemo(() => town?.data.hotspots ?? [], [town]);
  const entranceOf = useCallback(
    (key: string) => hotspots.find((e) => e.key === key),
    [hotspots],
  );

  /* ---------------- 场景初始化（原型初始化段 1:1，挂进 effect） ---------------- */
  const occluderSpans = useMemo(buildOccluderSpans, []);
  const ready = !!(town && builders);

  // 场景搭建：backdrop clipPath、architecture SVG、occluder 层、highlights、menu links
  useEffect(() => {
    if (!ready) return;
    const root = rootRef.current;
    const world = worldRef.current;
    const architecture = architectureRef.current;
    const people = peopleRef.current;
    const backdrop = backdropRef.current;
    const menuLinks = menuLinksRef.current;
    if (!root || !world || !architecture || !people || !backdrop || !menuLinks) return;

    const dayURL = town!.data.dayAsset;

    // 原型：底图挖出建筑本体（evenodd），建筑层从底图裁切 + 金色描边链接
    backdrop.style.clipPath = `path(evenodd, "M0 0H1652V925H0Z ${hotspots.map((e) => e.outlinePath).join(' ')}")`;
    architecture.innerHTML =
      `<defs>${hotspots.map((e) => `<clipPath id="clip-${e.key}"><path d="${e.outlinePath}"/></clipPath>`).join('')}</defs>` +
      hotspots
        .map((e) => {
          const aria = e.label;
          const isInternal = e.targetType === 'INTERNAL';
          const hrefAttr = isInternal ? e.href : e.targetType === 'EXTERNAL' ? e.href : '';
          return `<a class="architecture-link" data-building="${e.key}" ${
            e.targetType === 'COMING_SOON' ? 'aria-disabled="true"' : `href="${hrefAttr}"`
          } aria-label="${aria}"><image class="architecture-image" href="${dayURL}" width="1652" height="925" clip-path="url(#clip-${e.key})"/><path class="architecture-outline" d="${e.outlinePath}"/></a>`;
        })
        .join('');

    // 原型：朝路遮挡层（每 occluder 一个 SVG，zIndex=depth）
    const foreground = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    foreground.setAttribute('viewBox', '0 0 1652 925');
    foreground.classList.add('world-foreground');
    foreground.setAttribute('aria-hidden', 'true');
    const depthLayers: SVGSVGElement[] = [];
    ROAD_OCCLUDERS.forEach((e) => {
      const layer = foreground.cloneNode() as SVGSVGElement;
      layer.dataset.depth = e.key;
      layer.style.zIndex = String(e.depth);
      layer.innerHTML = `<defs><clipPath id="road-clip-${e.key}"><path d="${e.path}"/></clipPath></defs><image href="${dayURL}" width="1652" height="925" clip-path="url(#road-clip-${e.key})"/>`;
      world.append(layer);
      depthLayers.push(layer);
    });

    // 原型：导览聚焦高亮层（zIndex 1000）
    const highlights = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    highlights.setAttribute('viewBox', '0 0 1652 925');
    highlights.classList.add('world-highlights');
    highlights.setAttribute('aria-hidden', 'true');
    highlights.innerHTML = hotspots
      .map((e) => `<path data-highlight="${e.key}" class="architecture-outline" d="${e.outlinePath}"/>`)
      .join('');
    world.append(highlights);

    // 原型：菜单链接（同源配置派生）
    menuLinks.innerHTML = hotspots
      .map((e) => {
        const isInternal = e.targetType === 'INTERNAL';
        return `<a href="${isInternal ? e.href : e.href}" data-destination="${e.key}" ${
          e.targetType === 'COMING_SOON' ? 'aria-disabled="true"' : ''
        }><span>${e.label}</span><span aria-hidden="true">↗</span></a>`;
      })
      .join('');

    // 原型：夜灯（局部调制椭圆，radialGradient）
    const lightNodes = (
      [
        [578, 507, 25, 19, 0],
        [729, 440, 22, 27, 8],
      ] as const
    ).map(([x, y, r, period, offset], i) => {
      architecture.querySelector('defs')!.insertAdjacentHTML(
        'beforeend',
        `<radialGradient id="lamp-dim-${i}"><stop offset="0" stop-color="#080a09"/><stop offset=".35" stop-color="#080a09" stop-opacity=".85"/><stop offset="1" stop-color="#080a09" stop-opacity="0"/></radialGradient>`,
      );
      const node = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
      node.setAttribute('cx', String(x));
      node.setAttribute('cy', String(y));
      node.setAttribute('rx', String(r));
      node.setAttribute('ry', String(r));
      node.setAttribute('fill', `url(#lamp-dim-${i})`);
      node.setAttribute('opacity', '0');
      node.style.pointerEvents = 'none';
      node.dataset.lamp = String(i);
      architecture.append(node);
      return { node, period, offset };
    });
    lightNodesRef.current = lightNodes;

    // 首访自动导览（2026-09-18 需求，方案《首访导览与工牌排版_实现说明》任务 A）：
    // localStorage chronospeak-guide-seen 无记录 → 场景搭建完成后延迟 600ms 自动
    // 打开导览弹窗一次（内容/交互与手动打开完全一致——同一 openDialog/drawGuide
    // 路径，焦点入弹窗）；关闭后（close 事件统一走 dialogs effect）记忆已写入，
    // 此后不再自动弹出；清记忆即恢复。弹窗本身非动画，reduced-motion 不影响展示。
    let autoGuideTimer: ReturnType<typeof setTimeout> | undefined;
    // 首访欢迎信共存（2026-09-25，A3：信先、导览后）：letter-seen 无记录时导览延迟
    // 到欢迎信关闭（custom 事件）再弹；欢迎信不存在/已读过则照旧 600ms。
    const letterSeen = storage.get('chronospeak-letter-seen');
    const startAutoGuide = () => {
      if (storage.get('chronospeak-guide-seen')) return;
      autoGuideTimer = setTimeout(() => {
        if (document.querySelector('dialog[open]')) return;
        storage.set('chronospeak-guide-seen', '1');
        guideIndexRef.current = 0;
        drawGuide();
        openDialog(guideDialogRef.current, null);
      }, 600);
    };
    let onLetterDismissed: (() => void) | undefined;
    if (!letterSeen) {
      // 信组件挂载与否未知（可能拉取失败不渲染）——1.2s 后若信未出现则照常弹导览；
      // 若信在，监听其关闭事件补弹。
      autoGuideTimer = setTimeout(() => {
        if (document.querySelector('.welcome-letter') || document.querySelector('dialog[open]')) return;
        startAutoGuide();
      }, 1200);
      onLetterDismissed = () => startAutoGuide();
      window.addEventListener('town-letter-dismissed', onLetterDismissed);
    } else {
      startAutoGuide();
    }

    return () => {
      if (autoGuideTimer) clearTimeout(autoGuideTimer);
      if (onLetterDismissed) window.removeEventListener('town-letter-dismissed', onLetterDismissed);
      // React 重挂载清理（StrictMode 双调用安全）：移除动态层与灯
      depthLayers.forEach((l) => l.remove());
      highlights.remove();
      architecture.querySelectorAll('[data-lamp]').forEach((n) => n.remove());
      lightNodesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, town, locale]);

  const lightNodesRef = useRef<{ node: SVGEllipseElement; period: number; offset: number }[]>([]);

  /* ---------------- resize（原型 1:1：cover 缩放 + walker 命区反缩放） ---------------- */
  useEffect(() => {
    if (!ready) return;
    const root = rootRef.current;
    const world = worldRef.current;
    if (!root || !world) return;
    const resize = () => {
      const w = root.clientWidth;
      const h = root.clientHeight;
      const scale = Math.max(w / SIZE[0], h / SIZE[1]);
      const x = (w - SIZE[0] * scale) / 2;
      const y = (h - SIZE[1] * scale) / 2;
      world.style.transform = `translate(${x}px,${y}px) scale(${scale})`;
      document.querySelectorAll<HTMLElement>('.walker').forEach((b) => {
        b.style.width = `${Math.max(TOWN.WALKER_W, TOWN.WALKER_W / scale)}px`;
        b.style.height = `${Math.max(TOWN.WALKER_H, TOWN.WALKER_H / scale)}px`;
      });
    };
    const ro = new ResizeObserver(resize);
    ro.observe(root);
    resize();
    return () => ro.disconnect();
  }, [ready]);

  /* ---------------- 人物系统（原型 crowd + people 段 1:1） ---------------- */
  const crowdRef = useRef<ReturnType<typeof createCrowd> | null>(null);
  const peopleRef2 = useRef<PersonEntry[]>([]);
  // 人物 onclick 经 ref 调 openProfile（openProfile 定义在组件体后段——ref 转发
  // 解耦声明顺序；effect 每次渲染同步最新闭包）
  const openProfileRef = useRef<(b: PublicBuilder, trigger: HTMLElement | null) => Promise<void>>(async () => {});

  useEffect(() => {
    if (!ready || !builders) return;
    const people = peopleRef.current;
    if (!people) return;
    const records = [...builders];
    const crowd = createCrowd({ seed: crypto.getRandomValues(new Uint32Array(1))[0] });
    crowdRef.current = crowd;
    const entries: PersonEntry[] = [];
    crowd.people.forEach((motion, i) => {
      const b = records[i] ?? null;
      const button = document.createElement(b ? 'button' : 'span') as HTMLButtonElement | HTMLSpanElement;
      button.className = 'walker';
      button.dataset.resident = String(i);
      if (b) {
        (button as HTMLButtonElement).type = 'button';
        button.dataset.person = b.slug;
        button.setAttribute('aria-label', text('查看', 'View ') + (en ? b.name : b.name));
        button.title = en ? b.name : b.name;
      } else {
        button.classList.add('ambient-resident');
        button.setAttribute('aria-hidden', 'true');
      }
      button.innerHTML = personSVG(i);
      people.append(button);
      const svg = button.querySelector('svg');
      if (svg) {
        svg.style.width = `${15 + (i % 4)}px`;
        svg.style.height = `${26 + (i % 5)}px`;
      }
      if (b) {
        const marker = document.createElement('span');
        marker.className = 'person-marker';
        marker.setAttribute('aria-hidden', 'true');
        marker.style.bottom = `${34 + (i % 5)}px`;
        button.append(marker);
      }
      const person: PersonEntry = { button, record: b, motion: motion as PersonMotion, hover: false, focused: false };
      button.onpointerenter = () => (person.hover = true);
      button.onpointerleave = () => (person.hover = false);
      button.onfocus = () => (person.focused = true);
      button.onblur = () => (person.focused = false);
      // 原型 1:1：点击直接调 showProfile——不走 state（setState 同引用不触发
      // effect，同一人物关闭后再次点击会失灵）。openProfile 为 ref 稳定引用。
      if (b) (button as HTMLButtonElement).onclick = () => openProfileRef.current(b, button);
      entries.push(person);
    });
    peopleRef2.current = entries;
    return () => {
      entries.forEach((p) => p.button.remove());
      peopleRef2.current = [];
      crowdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, builders, en]);

  /* ---------------- highlight / guide / dialog（原型 1:1） ---------------- */
  const highlightRef = useRef<(key: string | null) => void>(() => {});
  const guideIndexRef = useRef(0);
  const triggerRef = useRef<HTMLElement | null>(null);
  const selectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    const root = rootRef.current;
    const architecture = architectureRef.current;
    const label = labelRef.current;
    if (!root || !architecture || !label) return;
    const highlights = root.querySelector<SVGSVGElement>('.world-highlights');

    const highlight = (key: string | null) => {
      architecture.querySelectorAll('[data-building]').forEach((a) => a.classList.toggle('is-lit', (a as SVGElement).dataset.building === key));
      highlights?.querySelectorAll('[data-highlight]').forEach((p) => p.classList.toggle('is-lit', (p as SVGElement).dataset.highlight === key));
      const item = key ? entranceOf(key) : undefined;
      label.hidden = !item;
      if (item) {
        const scale = worldScaleRef.current.scale;
        const ox = worldScaleRef.current.x;
        const oy = worldScaleRef.current.y;
        label.textContent = item.label;
        label.style.left = `${Math.max(80, Math.min(root.clientWidth - 80, item.labelAnchor.x * scale + ox))}px`;
        label.style.top = `${Math.max(95, Math.min(root.clientHeight - 90, item.labelAnchor.y * scale + oy))}px`;
      }
    };
    highlightRef.current = highlight;

    // 原型：建筑链接 hover/focus/click（SVG <a> 本体裁切）
    const links = Array.from(architecture.querySelectorAll<SVGAElement>('[data-building]'));
    links.forEach((a) => {
      a.setAttribute('tabindex', '0');
      const key = a.dataset.building ?? '';
      const onEnter = () => highlight(key);
      const onLeave = () => highlight(guideDialogRef.current?.open ? (hotspots[guideIndexRef.current]?.key ?? null) : (document.activeElement as HTMLElement | null)?.dataset?.building || null);
      const onFocus = () => highlight(key);
      const onBlur = () => highlight(guideDialogRef.current?.open ? (hotspots[guideIndexRef.current]?.key ?? null) : null);
      const onClick = (e: MouseEvent) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        if (selectedRef.current) return;
        selectedRef.current = key;
        highlight(key);
        // 埋点 building_opened（09 白名单：entranceKey/source/locale）
        track('building_opened', { entranceKey: key, source: 'map', locale });
        const item = entranceOf(key);
        const href = a.getAttribute('href');
        const target = item && (item.targetType === 'INTERNAL' || item.targetType === 'EXTERNAL') ? href : null;
        setTimeout(() => {
          if (target) {
            if (item?.targetType === 'EXTERNAL') window.open(target, '_blank', 'noopener');
            else navigate(target ?? '');
          }
          selectedRef.current = null;
        }, reduced.matches ? 0 : 180);
      };
      a.addEventListener('pointerenter', onEnter);
      a.addEventListener('pointerleave', onLeave);
      a.addEventListener('focus', onFocus);
      a.addEventListener('blur', onBlur);
      a.addEventListener('click', onClick);
      cleanupFnsRef.current.push(() => {
        a.removeEventListener('pointerenter', onEnter);
        a.removeEventListener('pointerleave', onLeave);
        a.removeEventListener('focus', onFocus);
        a.removeEventListener('blur', onBlur);
        a.removeEventListener('click', onClick);
      });
    });
    return () => {
      cleanupFnsRef.current.forEach((fn) => fn());
      cleanupFnsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, town, locale, navigate, entranceOf]);

  const cleanupFnsRef = useRef<(() => void)[]>([]);
  const worldScaleRef = useRef({ scale: 1, x: 0, y: 0 });

  /* ---------------- resize 同步 scale 到 ref（label 定位用） ---------------- */
  useEffect(() => {
    if (!ready) return;
    const root = rootRef.current;
    const world = worldRef.current;
    if (!root || !world) return;
    const resize = () => {
      const w = root.clientWidth;
      const h = root.clientHeight;
      const scale = Math.max(w / SIZE[0], h / SIZE[1]);
      worldScaleRef.current = { scale, x: (w - SIZE[0] * scale) / 2, y: (h - SIZE[1] * scale) / 2 };
      world.style.transform = `translate(${worldScaleRef.current.x}px,${worldScaleRef.current.y}px) scale(${scale})`;
      document.querySelectorAll<HTMLElement>('.walker').forEach((b) => {
        b.style.width = `${Math.max(TOWN.WALKER_W, TOWN.WALKER_W / scale)}px`;
        b.style.height = `${Math.max(TOWN.WALKER_H, TOWN.WALKER_H / scale)}px`;
      });
    };
    const ro = new ResizeObserver(resize);
    ro.observe(root);
    resize();
    return () => ro.disconnect();
  }, [ready]);

  /* ---------------- 动效主循环（原型 tick/paintPeople/syncMotion 1:1） ---------------- */
  const pausedRef = useRef(storage.get('chronospeak-paused') === '1');
  const [paused, setPaused] = useState(pausedRef.current);
  const [reducedState, setReducedState] = useState(reduced.matches);
  const nightRef = useRef(night);
  useEffect(() => {
    nightRef.current = night;
  }, [night]);
  const frameRef = useRef(0);
  const previousRef = useRef(0);
  const elapsedRef = useRef(0);

  const blocked = useCallback(
    () => pausedRef.current || reduced.matches || document.hidden || !!document.querySelector('dialog[open]'),
    [],
  );

  const syncMotion = useCallback(() => {
    const root = rootRef.current;
    const motionBtn = motionBtnRef.current;
    if (!root || !motionBtn) return;
    root.classList.toggle('is-paused', blocked());
    setIcon(motionBtn, pausedRef.current || reduced.matches ? playIcon : pauseIcon, text(pausedRef.current ? '恢复动效' : '暂停动效', pausedRef.current ? 'Resume motion' : 'Pause motion'));
    motionBtn.setAttribute('aria-pressed', String(pausedRef.current || reduced.matches));
    (motionBtn as HTMLButtonElement).disabled = reduced.matches;
    if (reduced.matches) {
      motionBtn.title = text('已遵循系统减少动态效果设置', 'System reduced motion is enabled');
      motionBtn.setAttribute('aria-label', motionBtn.title);
      lightNodesRef.current.forEach((l) => l.node.setAttribute('opacity', '0'));
      window.ChronoLanyard?.close();
      if (profileStaticRef.current) profileStaticRef.current.hidden = false;
    }
    setReducedState(reduced.matches);
    cancelAnimationFrame(frameRef.current);
    previousRef.current = 0;
    if (!blocked()) frameRef.current = requestAnimationFrame(tickRef.current);
  }, [blocked, text]);

  const tickRef = useRef<(now: number) => void>(() => {});

  useEffect(() => {
    tickRef.current = (now: number) => {
      const dt = previousRef.current ? Math.min((now - previousRef.current) / 1000, 0.1) : 0;
      previousRef.current = now;
      elapsedRef.current += dt;
      // paintPeople（原型 1:1）
      const people = peopleRef2.current;
      const crowd = crowdRef.current;
      if (crowd) {
        people.forEach((p) => (p.motion.held = p.hover || p.focused));
        crowd.update(dt);
        people.forEach((p) => {
          const held = p.hover || p.focused;
          const sample = p.motion;
          p.button.hidden = sample.state === 'indoors';
          p.button.style.opacity = String(sample.alpha);
          p.button.style.left = `${sample.x}px`;
          p.button.style.top = `${sample.y}px`;
          p.button.dataset.state = held ? 'pause' : sample.state;
          // Occluder depths only beat foot-y ordering when the walker is genuinely
          // "behind" the object: foot point inside the occluder's ground span, past
          // its base line. Pure-y ordering failed here for the slanted studio bases
          // and for walkers beside (not behind) the fountain ring.
          let z = Math.round(sample.y);
          if (sample.state !== 'entering' && sample.state !== 'exiting') {
            for (const occl of occluderSpans) {
              if (sample.x < occl.x0 || sample.x >= occl.x1) continue;
              const base = occl.edges.get(Math.round(sample.x));
              if (base !== undefined && sample.y < base) z = Math.max(z, occl.depth);
            }
          }
          p.button.style.zIndex = String(sample.state === 'entering' || sample.state === 'exiting' ? 386 : z);
          const phase = (sample.gait * 2 * Math.PI) / 1.15;
          const walking = !held && ['walk', 'entering', 'exiting'].includes(sample.state);
          p.button.style.setProperty('--stride', `${walking ? Math.sin(phase) * 17 : 0}deg`);
          p.button.style.setProperty('--bob', `${walking ? -Math.abs(Math.cos(phase)) * 0.5 : 0}px`);
          const figure = p.button.querySelector<HTMLElement>('.figure');
          if (figure && (sample.dx || sample.state === 'read-notice'))
            figure.style.transform = sample.dx < 0 || sample.state === 'read-notice' ? 'translateX(24px) scaleX(-1)' : '';
        });
      }
      // 夜灯波动（原型 1:1：低频不规则）
      lightNodesRef.current.forEach((l) => l.node.setAttribute('opacity', nightRef.current ? String(1 - lightLevel(elapsedRef.current, l.period, l.offset)) : '0'));
      frameRef.current = requestAnimationFrame(tickRef.current);
    };
    if (!ready) return;
    syncMotion();
    const onVis = () => syncMotion();
    const onReduced = () => syncMotion();
    document.addEventListener('visibilitychange', onVis);
    reduced.addEventListener('change', onReduced);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      reduced.removeEventListener('change', onReduced);
      cancelAnimationFrame(frameRef.current);
    };
  }, [ready, syncMotion, occluderSpans]);

  useEffect(() => {
    const onHide = () => cancelAnimationFrame(frameRef.current);
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const setStatus = useCallback((message: string) => {
    const el = statusRef.current;
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }, []);

  /* ---------------- 昼夜切换（原型 setTheme 1:1：预加载后切换） ---------------- */
  const loadingRef = useRef(false);
  const setTheme = useCallback(
    (value: boolean) => {
      if (loadingRef.current || !town) return;
      loadingRef.current = true;
      if (themeBtnRef.current) (themeBtnRef.current as HTMLButtonElement).disabled = true;
      const url = value ? town.data.nightAsset : town.data.dayAsset;
      const img = new Image();
      img.src = url;
      img
        .decode()
        .then(() => {
          const root = rootRef.current;
          const backdrop = backdropRef.current;
          const architecture = architectureRef.current;
          if (!root || !backdrop || !architecture) return;
          (backdrop as HTMLImageElement).src = url;
          [architecture, ...Array.from(root.querySelectorAll<SVGSVGElement>('.world-foreground'))].forEach((svg) =>
            svg.querySelectorAll('image').forEach((i) => i.setAttribute('href', url)),
          );
          lightNodesRef.current.forEach((l) => l.node.setAttribute('opacity', '0'));
          setNight(value);
          document.body.classList.toggle('night', value);
          storage.set('chronospeak-night', value ? '1' : '0');
          if (themeBtnRef.current) {
            setIcon(themeBtnRef.current, value ? sunIcon : moonIcon, text(value ? '切换日间' : '切换夜间', value ? 'Day mode' : 'Night mode'));
            themeBtnRef.current.setAttribute('aria-pressed', String(value));
          }
          setStatus('');
        })
        .catch(() => {
          setStatus(text('场景暂未加载，请通过目录访问。', 'Scene unavailable. Use town navigation.'));
        })
        .finally(() => {
          loadingRef.current = false;
          if (themeBtnRef.current) (themeBtnRef.current as HTMLButtonElement).disabled = false;
        });
    },
    [town, text, setStatus],
  );

  /* ---------------- 对话框（原型 dialogs/openDialog/drawGuide 1:1） ---------------- */
  const openDialog = useCallback(
    (dialog: HTMLDialogElement | null, source: HTMLElement | null) => {
      if (!dialog) return;
      triggerRef.current = source;
      highlightRef.current(null);
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      syncMotion();
    },
    [syncMotion],
  );

  const drawGuide = useCallback(() => {
    const e = hotspots[guideIndexRef.current];
    const count = guideCountRef.current;
    const title = guideTitleRef.current;
    const copyEl = guideCopyRef.current;
    const enter = guideEnterRef.current;
    if (!e || !count || !title || !copyEl || !enter) return;
    count.textContent = `${guideIndexRef.current + 1} / ${hotspots.length}`;
    title.textContent = e.label;
    copyEl.textContent = e.copy;
    if (e.targetType === 'COMING_SOON') {
      enter.removeAttribute('href');
      enter.setAttribute('aria-disabled', 'true');
    } else {
      enter.setAttribute('href', e.targetType === 'EXTERNAL' ? e.href : e.href);
      enter.removeAttribute('aria-disabled');
    }
    enter.textContent = text('进入此处', 'Enter');
    highlightRef.current(e.key);
  }, [hotspots, text]);

  // 对话框 close 事件（原型：锁滚动恢复 + 焦点回归 + ChronoLanyard.close）
  useEffect(() => {
    if (!ready) return;
    const dialogs = [menuDialogRef.current, guideDialogRef.current, profileDialogRef.current].filter(Boolean) as HTMLDialogElement[];
    const cleanups: (() => void)[] = [];
    dialogs.forEach((dialog) => {
      const closeBtns = Array.from(dialog.querySelectorAll<HTMLButtonElement>('[data-close]'));
      closeBtns.forEach((b) => {
        b.addEventListener('click', () => dialog.close());
      });
      const onClick = (e: MouseEvent) => {
        if (e.target === dialog) {
          const r = dialog.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
        }
      };
      const onClose = () => {
        document.body.style.overflow = '';
        highlightRef.current(null);
        window.ChronoLanyard?.close();
        triggerRef.current?.focus();
        syncMotion();
      };
      dialog.addEventListener('click', onClick);
      dialog.addEventListener('close', onClose);
      cleanups.push(() => {
        closeBtns.forEach((b) => b.removeEventListener('click', () => dialog.close()));
        dialog.removeEventListener('click', onClick);
        dialog.removeEventListener('close', onClose);
      });
    });
    closeBtnsRef.current = [];
    return () => cleanups.forEach((fn) => fn());
  }, [ready, syncMotion]);

  /* ---------------- 工牌弹层（原型 showProfile 1:1：动态 import + 三级降级） ---------------- */
  const profileRequestRef = useRef(0);
  const openProfile = useCallback(
    async (b: PublicBuilder, trigger: HTMLElement | null) => {
      const dialog = profileDialogRef.current;
      if (!dialog) return;
      const request = ++profileRequestRef.current;
      if (profileNameRef.current) profileNameRef.current.textContent = en ? b.name : b.name;
      if (profileStaticNameRef.current) profileStaticNameRef.current.textContent = en ? b.name : b.name;
      if (profileRoleRef.current) profileRoleRef.current.textContent = en ? b.role : b.role;
      if (profileStatementRef.current) profileStatementRef.current.textContent = en ? b.statement : b.statement;
      if (profileLinkRef.current) {
        // 详情二级页已下线（2026-09-11）：按钮改去创作人之家列表页，
        // 带 ?builder= 索引让波轮直接定位到该人物（slug 与数字均被解析）。
        profileLinkRef.current.hidden = false;
        profileLinkRef.current.textContent = text('创作人之家', 'Builders’ House');
        profileLinkRef.current.setAttribute('href', `/${locale}/builders?builder=${b.slug}`);
      }
      // 占位卡不再在加载期展示：等 3D 工牌就绪直接出现（reduced-motion 由
      // syncMotion、加载失败由下方降级分支负责显示静态卡）。
      if (profileStaticRef.current) profileStaticRef.current.hidden = true;
      if (profileCanvasRef.current) profileCanvasRef.current.classList.remove('is-ready');
      openDialog(dialog, trigger);
      // 埋点 builder_card_opened（09 白名单：builderSlug/source/locale）
      track('builder_card_opened', { builderSlug: b.slug, source: 'town', locale });
      if (reduced.matches) return;
      try {
        const [{ badgeFace }, { default: mountLanyard }] = await Promise.all([
          import('../features/town/badge/badge-texture.js'),
          import('../features/town/badge/lanyard-mount'),
        ]);
        if (!dialog.open || request !== profileRequestRef.current || reduced.matches) return;
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl2');
        if (!gl) {
          if (profileStaticRef.current) profileStaticRef.current.hidden = false;
          return;
        }
        const index = (builders ?? []).findIndex((x) => x.slug === b.slug);
        // 原型 DTO 双语 {zh,en}；API 投影已按 locale 解析为单语 string——按当前语言包装。
        // 2026-09-22 任务 B：badgeFace 增加 locale 参数——贴图主信息区（姓名/身份/宣言）
        // 跟随当前语言排版；语言切换重合成由 openProfile 依赖 locale 重建保证。
        const badgeInput = {
          name: { zh: b.name, en: b.name },
          role: { zh: b.role, en: b.role },
          statement: { zh: b.statement, en: b.statement },
          photo: b.photoUrl,
        };
        const [frontImage, backImage] = await Promise.all([
          badgeFace(badgeInput, index, 'front', locale),
          badgeFace(badgeInput, index, 'back', locale),
        ]);
        if (!dialog.open || request !== profileRequestRef.current) return;
        mountLanyard(profileCanvasRef.current!, {
          frontImage,
          backImage,
          onReady: () => {
            if (dialog.open && request === profileRequestRef.current && !reduced.matches) {
              if (profileStaticRef.current) profileStaticRef.current.hidden = true;
              profileCanvasRef.current?.classList.add('is-ready');
            }
          },
        });
      } catch {
        if (dialog.open && request === profileRequestRef.current && profileStaticRef.current) profileStaticRef.current.hidden = false;
      }
    },
    [builders, en, locale, openDialog, text],
  );

  useEffect(() => {
    openProfileRef.current = openProfile;
  }, [openProfile]);

  /* ---------------- 控件初始化（图标/事件 1:1） ---------------- */
  useEffect(() => {
    if (!ready) return;
    if (openMenuBtnRef.current) setIcon(openMenuBtnRef.current, menuIcon, text('小镇导航', 'Town navigation'));
    if (openGuideBtnRef.current) setIcon(openGuideBtnRef.current, guideIcon, text('建筑导览', 'Town guide'));
    if (prevBtnRef.current) setIcon(prevBtnRef.current, leftIcon, text('上一处', 'Previous'));
    if (nextBtnRef.current) setIcon(nextBtnRef.current, rightIcon, text('下一处', 'Next'));
    document.querySelectorAll<HTMLButtonElement>('.town-dialog [data-close]').forEach((b) => setIcon(b, closeIcon, text('关闭', 'Close')));
    if (profileDialogRef.current) {
      const c = profileDialogRef.current.querySelector<HTMLButtonElement>('[data-close]');
      if (c) setIcon(c, closeIcon, text('关闭', 'Close'));
    }
    if (themeBtnRef.current) {
      setIcon(themeBtnRef.current, night ? sunIcon : moonIcon, text(night ? '切换日间' : '切换夜间', night ? 'Day mode' : 'Night mode'));
      themeBtnRef.current.setAttribute('aria-pressed', String(night));
    }
    // 语言按钮文案（原型 data-language-toggle：en 时显示「中文」）
    if (langBtnRef.current) langBtnRef.current.textContent = en ? '中文' : 'EN';
    // 菜单标题
    const menuTitle = document.getElementById('menu-title');
    if (menuTitle) menuTitle.textContent = text('小镇导航', 'Town navigation');
  }, [ready, night, en, text, town]);

  // 底图 alt 双语
  useEffect(() => {
    if (backdropRef.current) backdropRef.current.alt = text('Chronuspeak 创作小镇全景', 'Chronuspeak Creative Town panorama');
  }, [text]);

  // 控件点击（原型 onclick 1:1）
  const onToggleTheme = useCallback(() => setTheme(!night), [night, setTheme]);
  const onToggleMotion = useCallback(() => {
    pausedRef.current = !pausedRef.current;
    storage.set('chronospeak-paused', pausedRef.current ? '1' : '0');
    setPaused(pausedRef.current);
    syncMotion();
  }, [syncMotion]);

  /* ---------------- 初次昼夜应用（原型末行 setTheme(storage…) 1:1） ---------------- */
  const initialNightApplied = useRef(false);
  useEffect(() => {
    if (!town || initialNightApplied.current) return;
    initialNightApplied.current = true;
    if (night) {
      // 初始即夜间：直接换源（与原型一致走 setTheme 预加载路径）
      setTheme(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [town]);

  // 数据失败提示（原型 status 文案）
  useEffect(() => {
    if (failed) setStatus(text('场景暂未加载，请通过目录访问。', 'Scene unavailable. Use town navigation.'));
  }, [failed, setStatus, text]);


  return (
    <main className='live-town' data-live-town ref={rootRef as React.RefObject<HTMLElement>}>
      <WelcomeLetter />
      <a className='skip' href='#town-menu-button'>
        {text('跳至小镇导航', 'Skip to town navigation')}
      </a>
      <h1 className='sr-only'>Chronuspeak Tech {text('创作小镇', 'Creative Town')}</h1>
      <div className='town-world' data-world ref={worldRef}>
        <img
          className='world-backdrop'
          data-backdrop
          ref={backdropRef}
          src={night ? (town?.data.nightAsset ?? '/town/night.avif') : (town?.data.dayAsset ?? '/town/day.avif')}
          width={1652}
          height={925}
          alt={text('Chronuspeak 创作小镇全景', 'Chronuspeak Creative Town panorama')}
          fetchPriority='high'
          onError={() => setStatus(text('场景暂未加载，请通过目录访问。', 'Scene unavailable. Use town navigation.'))}
        />
        <svg className='world-architecture' data-architecture viewBox='0 0 1652 925' aria-label={text('小镇建筑', 'Town buildings')} ref={architectureRef} />
        <div className='world-people' data-people ref={peopleRef} />
      </div>
      <a className='town-brand' href={`/${locale}`}>
        Chronuspeak
      </a>
      <div className='town-controls'>
        <button
          type='button'
          data-language-toggle
          ref={langBtnRef}
          onClick={() => {
            track('language_changed', { from: locale, to: en ? 'zh-CN' : 'en', path: `/${locale}` });
            navigate(en ? '/zh-CN' : '/en');
          }}
          title={text('切换语言', 'Switch language')}
          aria-label={text('切换语言', 'Switch language')}
        >
          {en ? '中文' : 'EN'}
        </button>
        <button type='button' data-theme ref={themeBtnRef} onClick={onToggleTheme} aria-label={text('切换夜间', 'Switch to night')} title={text('切换夜间', 'Switch to night')} />
        <button
          type='button'
          data-motion
          ref={motionBtnRef}
          onClick={onToggleMotion}
          aria-label={text('暂停动效', 'Pause motion')}
          title={text('暂停动效', 'Pause motion')}
          aria-pressed={paused || reducedState}
          disabled={reducedState}
        />
      </div>
      <div className='town-bottom'>
        <button
          type='button'
          id='town-menu-button'
          data-open-menu
          ref={openMenuBtnRef}
          aria-haspopup='dialog'
          aria-label={text('打开小镇导航', 'Open town navigation')}
          title={text('小镇导航', 'Town navigation')}
          onClick={(e) => openDialog(menuDialogRef.current, e.currentTarget)}
        />
        <button
          type='button'
          data-open-guide
          ref={openGuideBtnRef}
          aria-haspopup='dialog'
          aria-label={text('打开建筑导览', 'Open town guide')}
          title={text('建筑导览', 'Town guide')}
          onClick={(e) => {
            openDialog(guideDialogRef.current, e.currentTarget);
            drawGuide();
          }}
        />
      </div>
      <p className='town-status' data-status role='status' hidden ref={statusRef} />
      <p className='town-draft' data-draft hidden={!en}>
        English draft · 未审核
      </p>
      <div className='town-label' data-label hidden ref={labelRef} />
      <noscript>
        <nav className='town-noscript'>
          <a href='/zh-CN/builders'>创作人之家</a>
          <a href='/zh-CN/notices'>公告栏</a>
          <a href='/zh-CN/contact'>联络站</a>
          <a href='/zh-CN/about'>时光广场</a>
        </nav>
      </noscript>

      {/* 小镇导航弹窗（TownMenuDialog） */}
      <dialog className='town-dialog town-menu' data-menu aria-labelledby='menu-title' ref={menuDialogRef}>
        <div className='town-dialog-head'>
          <h2 id='menu-title'>小镇导航</h2>
          <button type='button' data-close aria-label={text('关闭', 'Close')} />
        </div>
        <nav data-menu-links ref={menuLinksRef} />
      </dialog>

      {/* 建筑导览弹窗（TownGuideDialog：四步导览，步数=入口配置数） */}
      <dialog className='town-dialog town-guide' data-guide aria-labelledby='guide-title' ref={guideDialogRef}>
        <div className='town-dialog-head'>
          <span data-guide-count ref={guideCountRef} />
          <button type='button' data-close aria-label={text('关闭', 'Close')} />
        </div>
        <h2 id='guide-title' data-guide-title ref={guideTitleRef} />
        <p data-guide-copy ref={guideCopyRef} />
        <div className='town-guide-actions'>
          <button
            type='button'
            data-prev
            ref={prevBtnRef}
            aria-label={text('上一处', 'Previous')}
            onClick={() => {
              guideIndexRef.current = (guideIndexRef.current + hotspots.length - 1) % hotspots.length;
              drawGuide();
            }}
          />
          <button
            type='button'
            data-next
            ref={nextBtnRef}
            aria-label={text('下一处', 'Next')}
            onClick={() => {
              guideIndexRef.current = (guideIndexRef.current + 1) % hotspots.length;
              drawGuide();
            }}
          />
          {/* 初始 href = 首个热点目标（弹窗打开时 drawGuide 按 era 覆盖；爬虫可见性兜底，M4 SEO） */}
          <a className='button' data-guide-enter ref={guideEnterRef} href={`/${locale}/about`}>
            {text('进入此处', 'Enter')}
          </a>
        </div>
      </dialog>

      {/* 工牌弹层（BuilderBadgeDialog：全屏） */}
      <dialog className='town-dialog town-profile' data-profile aria-labelledby='profile-name' ref={profileDialogRef}>
        <div className='profile-signature'>Chronuspeak Tech</div>
        <div className='profile-edition'>TIME IS NOT LINEAR, STORIES ARE ETERNAL.</div>
        <button type='button' className='profile-close' data-close aria-label={text('关闭工牌', 'Close badge')} />
        <div className='profile-canvas' data-canvas ref={profileCanvasRef} />
        <div className='profile-static' data-static aria-hidden='true' ref={profileStaticRef}>
          <span>CHRONUSPEAK</span>
          <strong data-static-name ref={profileStaticNameRef} />
          <small>BUILDER</small>
        </div>
        <div className='profile-copy'>
          <p data-profile-role ref={profileRoleRef} />
          <h2 id='profile-name' ref={profileNameRef} />
          <p data-profile-statement ref={profileStatementRef} />
          <a className='button' data-profile-link ref={profileLinkRef} href={`/${locale}/builders`}>
            {text('创作人之家', 'Builders’ House')}
          </a>
        </div>
      </dialog>
      {failed && (
        <p role='alert' className='town-status' style={{ zIndex: 30 }}>
          {text('场景暂未加载，请通过目录访问。', 'Scene unavailable. Use town navigation.')}
        </p>
      )}
      {!town && !failed && (
        <p className='town-status' style={{ zIndex: 30 }}>
          {text('小镇加载中…', 'Loading the town…')}
        </p>
      )}
      {town && <LocaleFallbackNotice fallbackUsed={(town as LocaleMeta).fallbackUsed} />}
      {builders === null && <DraftBadge text='' />}
    </main>
  );
}
